// reportsHub.service — the report library behind `/reports` ("Index + preview").
//
// The library is a CATALOGUE of sections, each holding a few entries. An entry
// is either one of the ten reports this module builds (`kind: 'report'`) or a
// link to an existing CRM page that already has its own download
// (`kind: 'link'` — the Accounts list and the User audit). Every entry carries
// the illustration it is drawn with (`art`) and the i18n keys of its preview.
// A report's preview shows a live snapshot of its figures (`reportSnapshot.service`);
// a link-out's shows one line naming the page it opens (`noteKey`).
//
// The paths are tested against the real route table (`usRoutes`), so an entry
// can never point at a page the router does not serve.
//
// ── WHICH ENTRY THE PREVIEW SHOWS ──────────────────────────────────────────
// A row is a LINK: clicking it opens the report. The preview FOLLOWS the
// pointer and keyboard focus instead — a row hovered or focused for
// {@link HUB_PREVIEW_INTENT_MS} becomes the pick (`usePreviewIntent`), and the
// pick stays when the pointer leaves the list, so the pane's buttons can be
// reached. The hub keeps that pick in state; everything else is derived here,
// so the rules are tested rather than buried in a component:
//
//   no pick yet      the first STARRED entry in view, else the most recently
//                    OPENED report in view, else the first entry in view;
//   a pick in view   that pick;
//   a pick hidden    (the tab changed under it) the first entry in view.
//
// This file does not import the report services: `routes.tsx` imports
// `REPORT_PATHS` from here, so it rides in the route bundle and must not drag
// the report builders in with it.

export const REPORT_PATHS = {
  hub: '/reports',
  billingSummary: '/reports/billing-summary',
  billingActivity: '/reports/billing-activity',
  billingExceptions: '/reports/billing-exceptions',
  leadFunnel: '/reports/lead-funnel',
  arAging: '/reports/ar-aging',
  revenueRegister: '/reports/revenue',
  paymentsRegister: '/reports/payments',
  usageVariance: '/reports/usage-variance',
  salesPerformance: '/reports/sales-performance',
  renewalRisk: '/reports/renewal-risk',
} as const;

/** The ten reports this module builds — the ids `useRecordReportOpen` records. */
export const REPORT_IDS = [
  'salesPerformance',
  'leadFunnel',
  'renewalRisk',
  'usageVariance',
  'billingSummary',
  'billingExceptions',
  'billingActivity',
  'arAging',
  'revenueRegister',
  'paymentsRegister',
] as const;
export type ReportId = (typeof REPORT_IDS)[number];

/** The two existing CRM pages the library links out to. */
export const LINK_ENTRY_IDS = ['accountsList', 'userAudit'] as const;
export type LinkEntryId = (typeof LINK_ENTRY_IDS)[number];

export type HubEntryId = ReportId | LinkEntryId;

export const HUB_ENTRY_IDS: readonly HubEntryId[] = [...REPORT_IDS, ...LINK_ENTRY_IDS];

/** Narrow a string to a library entry id (used when reading stored stars). */
export function isHubEntryId(value: string): value is HubEntryId {
  return (HUB_ENTRY_IDS as readonly string[]).includes(value);
}

/** Narrow a string to one of the ten report ids (used when reading stored opens). */
export function isReportId(value: string): value is ReportId {
  return (REPORT_IDS as readonly string[]).includes(value);
}

export type ReportSectionId = 'selling' | 'customers' | 'billing' | 'paid' | 'people';

/** A tab of the library: one section, or every section at once. */
export type HubSectionFilter = 'all' | ReportSectionId;

export const HUB_SECTION_FILTERS: readonly HubSectionFilter[] = [
  'all',
  'selling',
  'customers',
  'billing',
  'paid',
  'people',
];

/** The URL search param that holds the selected tab (`/reports?section=paid`). */
export const HUB_SECTION_PARAM = 'section';

// ─── Illustrations ──────────────────────────────────────────────────────────

/** The twelve illustrations `ReportArt` draws (geometry: `reportArt.service`). */
export type ReportArtKind =
  | 'bars'
  | 'funnel'
  | 'calendar'
  | 'scatter'
  | 'donut'
  | 'alert'
  | 'pulse'
  | 'stack'
  | 'line'
  | 'flow'
  | 'people'
  | 'shield';

/** The colourway an illustration is painted in (theme tokens: `utils/reportArtClasses`). */
export type ReportArtTone = 'navy' | 'sky' | 'amber' | 'mist';

export interface ReportArtSpec {
  readonly kind: ReportArtKind;
  readonly tone: ReportArtTone;
}

// ─── Catalogue ──────────────────────────────────────────────────────────────

/** One entry of the library. */
export interface ReportEntry {
  readonly id: HubEntryId;
  readonly section: ReportSectionId;
  /** i18n keys under `reports.hub.entries.<id>`. */
  readonly titleKey: string;
  /** The one-line description on the list row. */
  readonly descriptionKey: string;
  /** The preview pane's paragraph. */
  readonly summaryKey: string;
  /** A link-out's line in place of the snapshot ("Opens the existing Accounts page."); `null` for a report. */
  readonly noteKey: string | null;
  /** The preview's primary button: "Open {{report}}", or a link-out's own "Open Accounts". */
  readonly openKey: string;
  readonly to: string;
  /** `link` = an existing CRM page, not a report built in this module. */
  readonly kind: 'report' | 'link';
  readonly art: ReportArtSpec;
}

export interface ReportSection {
  readonly id: ReportSectionId;
  readonly titleKey: string;
  readonly entries: readonly ReportEntry[];
}

function entryKeys(id: HubEntryId): Pick<ReportEntry, 'titleKey' | 'descriptionKey' | 'summaryKey'> {
  const base = `reports.hub.entries.${id}`;
  return {
    titleKey: `${base}.title`,
    descriptionKey: `${base}.description`,
    summaryKey: `${base}.summary`,
  };
}

function report(id: ReportId, section: ReportSectionId, to: string, art: ReportArtSpec): ReportEntry {
  return {
    id,
    section,
    ...entryKeys(id),
    noteKey: null,
    openKey: 'reports.hub.openReport',
    to,
    kind: 'report',
    art,
  };
}

function linkOut(id: LinkEntryId, to: string, art: ReportArtSpec): ReportEntry {
  return {
    id,
    section: 'people',
    ...entryKeys(id),
    noteKey: `reports.hub.entries.${id}.note`,
    openKey: `reports.hub.entries.${id}.open`,
    to,
    kind: 'link',
    art,
  };
}

/** The full catalogue, in display order. */
export const REPORT_CATALOGUE: readonly ReportSection[] = [
  {
    id: 'selling',
    titleKey: 'reports.hub.sections.selling',
    entries: [
      report('salesPerformance', 'selling', REPORT_PATHS.salesPerformance, { kind: 'bars', tone: 'navy' }),
      report('leadFunnel', 'selling', REPORT_PATHS.leadFunnel, { kind: 'funnel', tone: 'sky' }),
    ],
  },
  {
    id: 'customers',
    titleKey: 'reports.hub.sections.customers',
    entries: [
      report('renewalRisk', 'customers', REPORT_PATHS.renewalRisk, { kind: 'calendar', tone: 'amber' }),
      report('usageVariance', 'customers', REPORT_PATHS.usageVariance, { kind: 'scatter', tone: 'navy' }),
    ],
  },
  {
    id: 'billing',
    titleKey: 'reports.hub.sections.billing',
    entries: [
      report('billingSummary', 'billing', REPORT_PATHS.billingSummary, { kind: 'donut', tone: 'sky' }),
      report('billingExceptions', 'billing', REPORT_PATHS.billingExceptions, { kind: 'alert', tone: 'navy' }),
      report('billingActivity', 'billing', REPORT_PATHS.billingActivity, { kind: 'pulse', tone: 'sky' }),
    ],
  },
  {
    id: 'paid',
    titleKey: 'reports.hub.sections.paid',
    entries: [
      report('arAging', 'paid', REPORT_PATHS.arAging, { kind: 'stack', tone: 'navy' }),
      report('revenueRegister', 'paid', REPORT_PATHS.revenueRegister, { kind: 'line', tone: 'sky' }),
      report('paymentsRegister', 'paid', REPORT_PATHS.paymentsRegister, { kind: 'flow', tone: 'navy' }),
    ],
  },
  {
    id: 'people',
    titleKey: 'reports.hub.sections.people',
    entries: [
      linkOut('accountsList', '/accounts', { kind: 'people', tone: 'mist' }),
      linkOut('userAudit', '/users/audit', { kind: 'shield', tone: 'mist' }),
    ],
  },
];

/** The i18n key of a tab's label (also the preview's section kicker). */
export function sectionTabKey(section: HubSectionFilter): string {
  return `reports.hub.sections.${section}`;
}

/** Read the `?section=` param. Anything absent, unknown or hand-edited is "All". */
export function parseHubSection(value: string | null): HubSectionFilter {
  return HUB_SECTION_FILTERS.find((s) => s === value) ?? 'all';
}

/**
 * The search params with `section` set. "All" is the default, so it drops the
 * param rather than writing `?section=all`. Other params are kept.
 */
export function withHubSection(params: URLSearchParams, section: HubSectionFilter): URLSearchParams {
  const next = new URLSearchParams(params);
  if (section === 'all') next.delete(HUB_SECTION_PARAM);
  else next.set(HUB_SECTION_PARAM, section);
  return next;
}

/** The entries a tab lists, in catalogue order. */
export function entriesInSection(
  section: HubSectionFilter,
  catalogue: readonly ReportSection[] = REPORT_CATALOGUE,
): ReportEntry[] {
  return catalogue.filter((s) => section === 'all' || s.id === section).flatMap((s) => s.entries);
}

// ─── Selection ──────────────────────────────────────────────────────────────

/** When each report was last opened, as ISO instants. */
export type ReportOpenTimes = Partial<Readonly<Record<ReportId, string>>>;

export interface HubSelectionInput {
  /** The entries in view (the current tab), in display order. */
  readonly entries: readonly ReportEntry[];
  /** The operator's explicit pick, or `null` before they have made one. */
  readonly selectedId: HubEntryId | null;
  /** Starred entry ids. */
  readonly starred: readonly HubEntryId[];
  readonly opened: ReportOpenTimes;
}

/** The report in view that was opened most recently, or `null` if none has been. */
export function mostRecentlyOpened(entries: readonly ReportEntry[], opened: ReportOpenTimes): ReportEntry | null {
  let best: ReportEntry | null = null;
  let bestAt = Number.NEGATIVE_INFINITY;
  for (const entry of entries) {
    if (!isReportId(entry.id)) continue;
    const at = Date.parse(opened[entry.id] ?? '');
    if (Number.isFinite(at) && at > bestAt) {
      best = entry;
      bestAt = at;
    }
  }
  return best;
}

/** The default pick: the first starred entry in view, else the most recently opened, else the first. */
export function defaultHubSelection(input: Omit<HubSelectionInput, 'selectedId'>): ReportEntry | null {
  const starred = input.entries.find((e) => input.starred.includes(e.id));
  if (starred !== undefined) return starred;
  return mostRecentlyOpened(input.entries, input.opened) ?? input.entries[0] ?? null;
}

/** The entry the preview shows (see the header for the three rules). */
export function resolveHubSelection(input: HubSelectionInput): ReportEntry | null {
  if (input.selectedId === null) return defaultHubSelection(input);
  return input.entries.find((e) => e.id === input.selectedId) ?? input.entries[0] ?? null;
}

/**
 * The pick to keep after switching to a tab listing `entries`: the current one
 * if that tab still shows it, otherwise the tab's first entry.
 */
export function selectionAfterSectionChange(
  entries: readonly ReportEntry[],
  currentId: HubEntryId | null,
): HubEntryId | null {
  if (currentId !== null && entries.some((e) => e.id === currentId)) return currentId;
  return entries[0]?.id ?? null;
}

// ─── Layout ─────────────────────────────────────────────────────────────────

/** Tailwind's `lg`: from here the library shows the preview pane beside the list. */
export const HUB_PANE_MEDIA_QUERY = '(min-width: 1024px)';

/**
 * How long a row must stay hovered or focused before the preview moves to it.
 * Long enough that sweeping the pointer across the list to reach the pane does
 * not flicker it through every row; short enough to feel immediate.
 */
export const HUB_PREVIEW_INTENT_MS = 120;
